// Pan/zoom view shared by the ground radar and the radar scope (local metres → canvas pixels, north up).
export class View {
  cx = 0;
  cy = 0;
  /** Pixels per metre. */
  scale = 0.3;
  w = 800;
  h = 600;
  dpr = 1;
  version = 0;

  toScreen(x: number, y: number): [number, number] {
    return [this.w / 2 + (x - this.cx) * this.scale, this.h / 2 - (y - this.cy) * this.scale];
  }
  toWorld(sx: number, sy: number): { x: number; y: number } {
    return { x: this.cx + (sx - this.w / 2) / this.scale, y: this.cy - (sy - this.h / 2) / this.scale };
  }
  zoomAt(sx: number, sy: number, factor: number, min: number, max: number): void {
    const before = this.toWorld(sx, sy);
    this.scale = Math.max(min, Math.min(max, this.scale * factor));
    const after = this.toWorld(sx, sy);
    this.cx += before.x - after.x;
    this.cy += before.y - after.y;
    this.version++;
  }
  pan(dxPx: number, dyPx: number): void {
    this.cx -= dxPx / this.scale;
    this.cy += dyPx / this.scale;
    this.version++;
  }
  /** Sizes the canvas to its CSS box. Returns true if the size changed. */
  fit(canvas: HTMLCanvasElement): boolean {
    const r = canvas.getBoundingClientRect();
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = Math.max(50, Math.round(r.width)), h = Math.max(50, Math.round(r.height));
    if (w === this.w && h === this.h && dpr === this.dpr && canvas.width === Math.round(w * dpr)) return false;
    this.w = w; this.h = h; this.dpr = dpr;
    canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
    this.version++;
    return true;
  }
}

/** Mouse interaction: wheel zoom, drag pan, click and context click. */
export function attachPanZoom(canvas: HTMLCanvasElement, view: View, opts: {
  min: number; max: number;
  onClick?: (sx: number, sy: number, e: MouseEvent) => void;
  onContext?: (sx: number, sy: number, e: MouseEvent) => void;
  onMove?: (sx: number, sy: number) => void;
  /** The user zoomed or panned. */
  onInteract?: () => void;
}): () => void {
  let drag: { x: number; y: number; moved: boolean } | null = null;
  const local = (e: MouseEvent) => { const r = canvas.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top] as const; };
  const wheel = (e: WheelEvent) => { e.preventDefault(); opts.onInteract?.(); const [x, y] = local(e); view.zoomAt(x, y, Math.exp(-e.deltaY * 0.0015), opts.min, opts.max); };
  const down = (e: MouseEvent) => { if (e.button !== 0) return; drag = { x: e.clientX, y: e.clientY, moved: false }; };
  const move = (e: MouseEvent) => {
    const [x, y] = local(e);
    opts.onMove?.(x, y);
    if (!drag) return;
    const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
    if (!drag.moved && Math.hypot(dx, dy) < 4) return;
    drag.moved = true;
    opts.onInteract?.();
    view.pan(dx, dy);
    drag.x = e.clientX; drag.y = e.clientY;
  };
  const up = (e: MouseEvent) => {
    if (!drag) return;
    const wasClick = !drag.moved;
    drag = null;
    if (wasClick) { const [x, y] = local(e); opts.onClick?.(x, y, e); }
  };
  const ctx = (e: MouseEvent) => { e.preventDefault(); const [x, y] = local(e); opts.onContext?.(x, y, e); };
  canvas.addEventListener('wheel', wheel, { passive: false });
  canvas.addEventListener('mousedown', down);
  window.addEventListener('mousemove', move);
  window.addEventListener('mouseup', up);
  canvas.addEventListener('contextmenu', ctx);
  return () => {
    canvas.removeEventListener('wheel', wheel);
    canvas.removeEventListener('mousedown', down);
    window.removeEventListener('mousemove', move);
    window.removeEventListener('mouseup', up);
    canvas.removeEventListener('contextmenu', ctx);
  };
}

/** Top-view outline of an aircraft (nose at the origin, pointing up), scaled to its dimensions. */
export function silhouette(lengthM: number, spanM: number, fuselageM: number, tail: 'low' | 'T', engine: string): [number, number][] {
  const L = lengthM, S = spanM / 2, f = Math.max(1.2, fuselageM) / 2;
  if (engine === 'heli') {
    const r = L / 2;
    return [[0, 0], [f, -r * 0.3], [f, -r], [0.4, -L], [-0.4, -L], [-f, -r], [-f, -r * 0.3]];
  }
  const wingRoot = -L * 0.36, wingTipLe = -L * 0.5, wingTipTe = -L * 0.58, wingRootTe = -L * 0.55;
  const tailRoot = -L * 0.84, tailTip = -L * 0.95, ts = S * (tail === 'T' ? 0.32 : 0.36);
  return [
    [0, 0], [f * 0.8, -L * 0.06], [f, -L * 0.15], [f, wingRoot], [S, wingTipLe], [S, wingTipTe], [f, wingRootTe],
    [f * 0.8, tailRoot], [ts, tailTip], [ts, -L], [0, -L * 0.97], [-ts, -L], [-ts, tailTip], [-f * 0.8, tailRoot],
    [-f, wingRootTe], [-S, wingTipTe], [-S, wingTipLe], [-f, wingRoot], [-f, -L * 0.15], [-f * 0.8, -L * 0.06],
  ];
}
